import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQuery } from '@apollo/client'
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons'
import { clsx } from 'clsx'
import { observer } from 'mobx-react'

import { Card, CardBody, TertiaryButton } from 'Components/_theme'
import { Icon } from 'Components/Icon/Icon'

import { useModal } from 'Hooks'

import { formatRelativeTime } from 'Utilities'

import type {
  getRentalDeviceInfo,
  getRentalDeviceInfoVariables,
} from 'Constants/graphql/queries/detail/__generated__/getRentalDeviceInfo'
import { GET_RENTAL_DEVICE_INFO } from 'Constants/graphql/queries/detail/GetRental'

import {
  getComplianceStateTranslationId,
  getOverallDeviceHealthStatus,
  getStorageFreePct,
  isBatteryHealthWarning,
  isComplianceWarning,
  isEncryptionWarning,
  isStorageWarning,
  isSyncWarning,
} from './deviceHealth.utils'
import type { DeviceHealthCardProps } from './DeviceHealthCard.types'
import { DeviceHealthInfoTooltip } from './DeviceHealthInfoTooltip'
import { DeviceHealthModal } from './DeviceHealthModal'
import { DeviceHealthStatusBadge } from './DeviceHealthStatusBadge'

interface DeviceHealthCardRowProps {
  label: ReactNode
  value: ReactNode
  isWarning: boolean
}

const DeviceHealthCardRow = (props: DeviceHealthCardRowProps) => {
  const { label, value, isWarning } = props

  return (
    <div className="DeviceHealthCard-row">
      <span>{label}</span>

      <span
        className={clsx(
          'DeviceHealthCard-row-value',
          isWarning && 'DeviceHealthCard-row-value--warning',
        )}
      >
        <span
          className={clsx(
            'DeviceHealthCard-dot',
            isWarning && 'DeviceHealthCard-dot--warning',
          )}
        />

        {value}
      </span>
    </div>
  )
}

export const DeviceHealthCard = observer((props: DeviceHealthCardProps) => {
  const { rentalId, organisationId } = props
  const intl = useIntl()
  const { setModal } = useModal()

  const { data } = useQuery<getRentalDeviceInfo, getRentalDeviceInfoVariables>(
    GET_RENTAL_DEVICE_INFO,
    { variables: { organisationId, rentalId } },
  )

  const deviceInfo = data?.rental?.stocklistItem?.deviceInfo

  const handleOpenModal = () => {
    setModal({
      component: DeviceHealthModal,
      contentClassName: 'DeviceHealthModal-content',
      isOpen: true,
      props: { deviceInfo },
    })
  }

  if (!deviceInfo) {
    return null
  }

  const overallStatus = getOverallDeviceHealthStatus(deviceInfo)
  const storageFreePct = getStorageFreePct(deviceInfo)
  const isEncryptionCritical = isEncryptionWarning(deviceInfo)

  return (
    <Card className="DeviceHealthCard mb-4 mb-lg-6">
      <CardBody>
        <div className="DeviceHealthCard-header">
          <Icon
            className="DeviceHealthCard-header-icon"
            icon={faShieldCheck}
            size={14}
          />

          <h2 className="DeviceHealthCard-header-title">
            <FormattedMessage id="DeviceHealth_title" />
          </h2>

          <DeviceHealthStatusBadge status={overallStatus} />
        </div>

        <div className="DeviceHealthCard-rows">
          {deviceInfo.managementState && (
            <DeviceHealthCardRow
              isWarning={false}
              label={<FormattedMessage id="DeviceHealth_management" />}
              value={deviceInfo.managementState}
            />
          )}

          {deviceInfo.complianceState && (
            <DeviceHealthCardRow
              isWarning={isComplianceWarning(deviceInfo)}
              label={
                <>
                  <FormattedMessage id="DeviceHealth_compliance" />

                  <DeviceHealthInfoTooltip translationId="DeviceHealth_compliance_tooltip" />
                </>
              }
              value={
                <FormattedMessage
                  id={getComplianceStateTranslationId(
                    deviceInfo.complianceState,
                  )}
                />
              }
            />
          )}

          {typeof deviceInfo.battery?.healthScore === 'number' && (
            <DeviceHealthCardRow
              isWarning={isBatteryHealthWarning(deviceInfo.battery)}
              label={
                <>
                  <FormattedMessage id="DeviceHealth_battery_health" />

                  <DeviceHealthInfoTooltip translationId="DeviceHealth_battery_health_tooltip" />
                </>
              }
              value={
                <FormattedMessage
                  id="DeviceHealth_battery_health_score_value"
                  values={{ score: deviceInfo.battery.healthScore }}
                />
              }
            />
          )}

          {isEncryptionCritical && (
            <DeviceHealthCardRow
              isWarning
              label={
                <>
                  <FormattedMessage id="DeviceHealth_encryption" />

                  <DeviceHealthInfoTooltip translationId="DeviceHealth_encryption_tooltip" />
                </>
              }
              value={<FormattedMessage id="DeviceHealth_not_encrypted" />}
            />
          )}

          {typeof storageFreePct === 'number' && (
            <DeviceHealthCardRow
              isWarning={isStorageWarning(deviceInfo)}
              label={<FormattedMessage id="DeviceHealth_storage" />}
              value={
                <FormattedMessage
                  id="DeviceHealth_storage_free_pct"
                  values={{ pct: storageFreePct }}
                />
              }
            />
          )}

          {deviceInfo.lastSyncedAt && (
            <DeviceHealthCardRow
              isWarning={isSyncWarning(deviceInfo)}
              label={<FormattedMessage id="DeviceHealth_last_check_in" />}
              value={formatRelativeTime(deviceInfo.lastSyncedAt, intl.locale)}
            />
          )}
        </div>

        <TertiaryButton
          appearance="outline"
          className="DeviceHealthCard-view-full"
          onClick={handleOpenModal}
        >
          {intl.formatMessage({ id: 'DeviceHealth_view_full' })}
        </TertiaryButton>
      </CardBody>
    </Card>
  )
})
